import { ShieldCheck } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '../../lib/utils';
import { StatusPill, type StatusPillTone } from '../portal';

export type SidebarVerificationStatus =
  | 'NotStarted'
  | 'InProgress'
  | 'UnderReview'
  | 'Approved'
  | 'Rejected'
  | 'RequiresAdditionalInfo'
  | 'Loading';

export interface SidebarVerificationCard {
  status: SidebarVerificationStatus;
  title: string;
  badgeText: string;
  linkTo?: string;
  isActionable: boolean;
  show: boolean;
}

const BADGE_TONES: Record<SidebarVerificationStatus, StatusPillTone> = {
  NotStarted: 'required',
  InProgress: 'waiting',
  UnderReview: 'review',
  Approved: 'published',
  Rejected: 'rejected',
  RequiresAdditionalInfo: 'required',
  Loading: 'neutral',
};

interface VerificationRowProps {
  kybStatus: SidebarVerificationCard;
  isActiveRoute: (path: string) => boolean;
}

/** Compact KYB/KYC row styled like a nav item with a status pill. */
export function VerificationRow({
  kybStatus,
  isActiveRoute,
}: VerificationRowProps) {
  const navigate = useNavigate();

  if (!kybStatus.show) {
    return null;
  }

  const isActive = kybStatus.linkTo ? isActiveRoute(kybStatus.linkTo) : false;
  const isInteractive = kybStatus.isActionable && Boolean(kybStatus.linkTo);

  const navigateToKybLink = () => {
    if (isInteractive && kybStatus.linkTo) {
      navigate(kybStatus.linkTo);
    }
  };

  const activateOnEnterOrSpace = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      navigateToKybLink();
    }
  };

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : -1}
      onClick={navigateToKybLink}
      onKeyDown={activateOnEnterOrSpace}
      className={cn(
        'flex h-9 w-full items-center gap-2.5 rounded-xl pl-4 pr-2 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-950',
        isActive
          ? 'bg-seeker-900 font-bold text-white shadow-[inset_0_0.375px_0.375px_0_rgba(255,255,255,0.17)]'
          : 'font-medium text-seeker-400',
        isInteractive
          ? 'cursor-pointer hover:bg-seeker-900 hover:text-white'
          : 'cursor-default',
      )}
    >
      <ShieldCheck className="size-4 shrink-0" aria-hidden />
      <span
        title={kybStatus.title}
        className="min-w-0 flex-1 truncate text-sm leading-5"
      >
        {kybStatus.title}
      </span>
      {kybStatus.badgeText ? (
        <StatusPill tone={BADGE_TONES[kybStatus.status] ?? 'neutral'}>
          {kybStatus.badgeText}
        </StatusPill>
      ) : null}
    </div>
  );
}
